import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

/**
 * Per-subject visibility state for the Subsurface Explorer inset-map scoped
 * layers (INS-8658 / INS-9182).
 *
 * Keyed by the subject UWI so each well's inset map keeps its own toggle state,
 * fully isolated from the main Well Page map and from other subjects. All
 * layers default to ON:
 *   - Subsurface Explorer Wells: Nearby + Offset (INS-9182)
 *   - Subject well + Development Areas → Intersecting Units (mirrors the main map)
 *
 * In-memory (plain `atom`) rather than `atomWithStorage` — per-subject isolation
 * is the goal, and we don't want one subject's toggles to persist onto another.
 * These are the inset's OWN atoms, deliberately separate from the main Well Page
 * map's global atoms (`isUnitsVisibleAtom` / `isProducingVisibleAtom`).
 */
export interface ScopedLayersVisibility {
  nearby: boolean;
  offset: boolean;
  subject: boolean;
  units: boolean;
}

export const subsurfaceScopedLayersVisibilityAtomFamily = atomFamily(
  // The key (subject UWI) scopes each subject's state; the initial value is the
  // same for every key, so the param itself is not read.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (uwi: string) =>
    atom<ScopedLayersVisibility>({
      nearby: true,
      offset: true,
      subject: true,
      units: true,
    }),
  (a, b) => a === b
);
